import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { GeneSet, Test, SavedFilter } from '../types';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { messages } from './intlLabels';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { SummaryTestTable } from './SummaryTestTable';
import { useIntl, IntlShape } from 'react-intl';
import * as React from 'react';
import moment from 'moment';
import TableTitle from '@lifeomic/phc-web-toolkit/src/components/Table/TableTitle';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { sharedMessages } from '../sharedIntlLabels';
import { useSelectedSavedFilters } from '../useSelectedSavedFilters';
import { Chip } from '@lifeomic/chroma-react/components/Chip';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { useSavedFilters } from '../SavedFiltersContext';
import { Info } from '@lifeomic/chromicons';
import clsx from 'clsx';

const getSubtitle = ({
  test,
  formatMessage,
}: {
  test: Test;
  formatMessage: IntlShape['formatMessage'];
}) => {
  if (!test) {
    return formatMessage(messages.processing);
  }

  if (test.testType && test.indexedDate) {
    return formatMessage(messages.nameTypeDate, {
      name: test.name,
      type: test.testType,
      date: moment(test.indexedDate).format('YYYY-MM-DD'),
    });
  } else if (test.testType) {
    return formatMessage(messages.nameType, {
      name: test.name,
      type: test.testType,
    });
  }
  return test.name;
};

const useStyles = makeStyles(theme => ({
  root: {
    height: `calc(100vh - ${theme.pxToRem(258)})`,
    '&:not(:last-of-type)': {
      marginBottom: theme.spacing(4),
    },
    '& > div': {
      display: 'grid',
      gridTemplateColumns: `${theme.pxToRem(287)} 1fr`,
      gridTemplateRows: `${theme.pxToRem(51)} 1fr`,
      height: '100%',
      '& > div:first-of-type': {
        gridColumn: 'span 2',
      },
    },
  },
  noFilters: {
    '& > div': {
      gridTemplateColumns: `1fr 2fr`,
      '& > div:first-of-type': {
        gridColumn: 'unset',
      },
      '& > div:last-of-type': {
        gridColumn: 'span 2',
      },
    },
    '& $filtersBox': {
      borderBottom: `solid ${theme.pxToRem(1)} ${theme.palette.divider}`,
      borderRight: 'none',
    },
  },
  infoIcon: {
    color: theme.palette.primary.main,
    display: 'inline-flex',
    height: theme.pxToRem(18),
    width: theme.pxToRem(18),
  },
  filtersBox: {
    borderRight: `solid ${theme.pxToRem(1)} ${theme.palette.divider}`,
    padding: theme.spacing(2),
  },
  chipBox: {
    overflowX: 'auto',
  },
  overflow: {
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : theme.palette.black[50],
    maxHeight: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  tableContainer: {
    boxShadow: theme.boxShadows.table,
    maxHeight: '100%',
    maxWidth: '100%',
    overflowX: 'auto',
  },
}));

interface SummaryPanelAndTableProps {
  areGeneSetsFetching?: boolean;
  geneSets?: Array<GeneSet>;
  hasCompletedGeneSetsFetch?: boolean;
  test: Test;
}

export const SummaryPanelAndTable: React.FC<SummaryPanelAndTableProps> = ({
  areGeneSetsFetching,
  geneSets,
  hasCompletedGeneSetsFetch,
  test,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [savedFilters] = useSavedFilters();
  const { selectedSavedFilters, setSelectedSavedFilters } =
    useSelectedSavedFilters();

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  const filtersToDisplayAsSelects: Array<string> =
    selectedSavedFilters &&
    Object.keys(selectedSavedFilters).filter(
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      key => selectedSavedFilters[key]?.id !== 'default',
    );

  const hasNoDefaultFiltersSet =
    selectedSavedFilters &&
    Object.keys(selectedSavedFilters).every(
      // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
      key => selectedSavedFilters[key]?.id === 'default',
    );

  return (
    <Paper
      className={clsx(
        classes.root,
        hasNoDefaultFiltersSet ? classes.noFilters : undefined,
      )}
      padding={0}
    >
      <TableTitle title={getSubtitle({ test, formatMessage })}>
        <Box
          className={classes.filtersBox}
          direction="column"
          gap={2}
          fullWidth
          fullHeight
        >
          {filtersToDisplayAsSelects?.length > 0 && (
            <>
              <Text size="subbody" weight="bold">
                {formatMessage(messages.appliedFilters)}
              </Text>
              {filtersToDisplayAsSelects?.map(key => {
                // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                const selectedFilter: SavedFilter = selectedSavedFilters[key];
                // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
                const filters: SavedFilter[] = savedFilters[key];

                const handleOnChange = (id: string) => {
                  const filter = filters.find(filter => filter.id === id);
                  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                  setSelectedSavedFilters({ filter, key });
                };

                return (
                  <>
                    <Box direction="column">
                      <Select
                        // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
                        label={formatMessage(sharedMessages[key])}
                        onChange={handleOnChange}
                        value={selectedFilter.id}
                        fullWidth
                      >
                        {filters?.map((filter, index) => (
                          <SelectOption
                            key={index}
                            subtitle={
                              selectedFilter.id === filter.id
                                ? formatMessage(messages.currentDefault)
                                : undefined
                            }
                            title={filter.name}
                            value={filter.id}
                          />
                        ))}
                      </Select>
                      <Box className={classes.chipBox} fullWidth>
                        {selectedFilter?.params?.filters?.map(
                          (option, optionIndex) => {
                            return (
                              <Chip
                                key={optionIndex}
                                label={
                                  option.friendlyName ||
                                  (option.searchValue as string)
                                }
                              />
                            );
                          },
                        )}
                      </Box>
                    </Box>
                  </>
                );
              })}
            </>
          )}
          {hasNoDefaultFiltersSet && (
            <Box align="center" justify="flex-end" gap={1} fullWidth fullHeight>
              <Text size="caption">
                {formatMessage(messages.noDefaultFilters)}
              </Text>
              <Tooltip title={formatMessage(messages.noFiltersExplanation)}>
                <span style={{ display: 'inline-flex' }}>
                  <Info className={classes.infoIcon} />
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
        {areGeneSetsFetching && <DotLoader size={0} />}
        {!areGeneSetsFetching &&
          hasCompletedGeneSetsFetch &&
          selectedSavedFilters && (
            <div className={classes.overflow}>
              <div className={classes.tableContainer}>
                <SummaryTestTable
                  test={test}
                  geneSets={geneSets}
                  selectedSavedFilters={selectedSavedFilters}
                />
              </div>
            </div>
          )}
      </TableTitle>
    </Paper>
  );
};
